import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const EMPTY_BRANDING = { nome_agenzia: "", logo_url: "", header_subtitle: "" };

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(EMPTY_BRANDING);

  const fetchBranding = useCallback(async () => {
    try {
      const r = await api.get("/agency/public");
      setBranding(r.data || EMPTY_BRANDING);
    } catch {
      setBranding(EMPTY_BRANDING);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      fetchBranding();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [fetchBranding]);

  useEffect(() => {
    if (localStorage.getItem("access_token")) {
      fetchMe();
    } else {
      setUser(false);
      setLoading(false);
    }
  }, [fetchMe]);

  // Listen for branding updates (from Impostazioni save)
  useEffect(() => {
    const handler = () => fetchBranding();
    window.addEventListener("agency:updated", handler);
    // Ricarica quando la tab torna in primo piano (utente che rientra dopo
    // pausa / dopo aver fatto operazioni su un'altra tab / dopo il pagamento
    // Stripe). Evita polling ma copre i casi reali di stato out-of-date.
    const onVis = () => {
      if (document.visibilityState === "visible") fetchBranding();
    };
    document.addEventListener("visibilitychange", onVis);
    // Ricarica anche al ritorno del focus (finestra desktop).
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("agency:updated", handler);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", handler);
    };
  }, [fetchBranding]);

  const login = useCallback(async (username, password) => {
    const res = await api.post("/auth/login", { username, password });
    localStorage.setItem("access_token", res.data.access_token);
    // Hydrate user with full context (includes agency object, branding fields, ecc.).
    // Il payload di /auth/login non include l'oggetto agency, che è invece necessario per
    // decidere la visibilità dei link condizionali (es. nav-sospesi in Layout).
    let fullUser = res.data.user;
    try {
      const me = await api.get("/auth/me");
      fullUser = me.data;
    } catch (err) { /* fallback: teniamo l'utente base della login response */
      if (typeof console !== "undefined") console.debug("auth/me hydrate failed:", err?.message || err);
    }
    setUser(fullUser);
    fetchBranding();
    return fullUser;
  }, [fetchBranding]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout best-effort: server-side session may already be invalid.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed (non-blocking):", err?.message || err);
      }
    }
    localStorage.removeItem("access_token");
    setUser(false);
    setBranding(EMPTY_BRANDING);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh: fetchMe, refreshMe: fetchMe, branding, isReadOnly: user?.role === "consultazione" }),
    [user, loading, login, logout, fetchMe, branding],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
